<template>
  <section disabled="performingRequest">
    <va-modal
      :value="showModal"
      ref="updatePatient"
      :okText="$t('references.inputs.save')"
      :cancelText="$t('modal.cancel')"
      :noOutsideDismiss="true"
      :okDisabled="isLoading || anyErrors"
      @ok="submitForm"
      @cancel="onCancel"
      >
        <div slot="header" class="modal-title">
          <span >{{ $t('dashboard_new_evollu.modal.title_edit_patient') }}</span>
        </div>

        <form class="evollu-form">
          <div class="row">
            <div class="col-12">
              <label class="form-label">{{$t('dashboard_new_evollu.titles.title')}}</label>
              <multiselect
                v-model="patientDetailsToEdit.title"
                :options="titles"
                :placeholder="$t('dashboard_new_evollu.titles.title')"
              />
              <div class="text-danger" v-if="userForm.errors.has('title')">
                <small>
                  {{$t(userForm.errors.get('title'))}}
                </small>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <va-input
                id="first-name-input"
                v-model="patientDetailsToEdit.firstName"
                :label="$t('dashboard_new_evollu.titles.first_name')"
                :placeholder="$t('dashboard_new_evollu.titles.first_name')"
                :error="userForm.errors.has('firstName')"
                :error-messages="[$t(userForm.errors.get('firstName'))]"
                @focus="userForm.errors.clear('firstName')"
                />
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <va-input
                id="surname-input"
                v-model="patientDetailsToEdit.surname"
                :label="$t('dashboard_new_evollu.titles.last_name')"
                :placeholder="$t('dashboard_new_evollu.titles.last_name')"
                :error="userForm.errors.has('surname')"
                :error-messages="[$t(userForm.errors.get('surname'))]"
                @focus="userForm.errors.clear('surname')"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <va-date-picker
                :config="{
                  enableTime: false,
                  dateFormat: 'Y-m-d',
                  maxDate: moment().toDate(),
                }"
                v-model="patientDetailsToEdit.dateOfBirth"
                :label="$t('dashboard_new_evollu.titles.birthdate')"
                :placeholder="$t('dashboard_new_evollu.titles.birthdate')"
                :error="userForm.errors.has('dateOfBirth')"
                :error-messages="[$t(userForm.errors.get('dateOfBirth'))]"
                @focus="userForm.errors.clear('dateOfBirth')"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <va-input
                id="address-input"
                v-model="patientDetailsToEdit.address"
                :label="$t('dashboard_new_evollu.titles.address')"
                :placeholder="$t('dashboard_new_evollu.titles.address')"
                :error="userForm.errors.has('address')"
                :error-messages="[$t(userForm.errors.get('address'))]"
                @focus="userForm.errors.clear('address')"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-3">
              <va-input
                id="zipprefix-input"
                v-model="patientDetailsToEdit.zipPrefix"
                :label="$t('dashboard_new_evollu.titles.zipPrefix')"
                :placeholder="$t('dashboard_new_evollu.titles.zipPrefix')"
                :error="userForm.errors.has('zipPrefix')"
                :error-messages="[$t(userForm.errors.get('zipPrefix'))]"
                @focus="userForm.errors.clear('zipPrefix')"
              />
            </div>

            <div class="col-2">
              <va-input
                id="zipsufix-input"
                v-model="patientDetailsToEdit.zipSufix"
                :label="$t('dashboard_new_evollu.titles.zipSufix')"
                :placeholder="$t('dashboard_new_evollu.titles.zipSufix')"
                :error="userForm.errors.has('zipSufix')"
                :error-messages="[$t(userForm.errors.get('zipSufix'))]"
                @focus="userForm.errors.clear('zipSufix')"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <va-input
                id="email-input"
                v-model="patientDetailsToEdit.email"
                :label="$t('dashboard_new_evollu.titles.email')"
                :placeholder="$t('dashboard_new_evollu.titles.email')"
                :error="userForm.errors.has('email')"
                :error-messages="[$t(userForm.errors.get('email'))]"
                @focus="userForm.errors.clear('email')"
                />
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <label class="form-label">{{$t('dashboard_new_evollu.titles.mobile')}}</label>
              <vue-tel-input
                inputId="phone"
                style="width: inherit;"
                v-model="patientDetailsToEdit.fullMobileNumber"
                maxlength="20"
                :validCharactersOnly="true"
                :mode="'international'"
                :preferredCountries="countriestel.allowed"
                :disabledFetchingCountry="true"
                :defaultCountry="defaultCountrySelection"
                :label="$t('dashboard_new_evollu.titles.mobile')"
                :placeholder="$t('dashboard_new_evollu.titles.mobile')"
                @validate="telephoneValidation">
              </vue-tel-input>
              <span class="small text-danger" v-show="patientDetailsToEdit.fullMobileNumber !== '' && patientDetailsToEdit.fullMobileNumber !== null && invalidMobilePhone">
                {{ $t('patients.errors.invalidPhone') }}
              </span>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <label class="form-label">{{$t('dashboard_new_evollu.titles.country')}}</label>
              <multiselect
                v-model="patientDetailsToEdit.country"
                :options="countries"
                :placeholder="$t('dashboard_new_evollu.titles.country')"
              />
            </div>
          </div>

          <div class="text-center" v-if="isLoading">
            <LoadingAnimation></LoadingAnimation>
          </div>

          <div class="row" v-if="!isLoading && patientDetailsToEdit.country === 'Portugal'">
            <div class="col-12">
              <label class="form-label">{{$t('dashboard_new_evollu.titles.district')}}</label>
              <multiselect
                v-model="patientDetailsToEdit.municipio"
                :options="districts"
                label="districtName"
                track-by="districtID"
                :placeholder="$t('dashboard_new_evollu.titles.district')"
                @input="onChangeDistrict"
              />
            </div>
          </div>

          <div class="row" v-if="!isLoading && patientDetailsToEdit.country === 'Portugal'">
            <div class="col-12">
              <label class="form-label">{{$t('dashboard_new_evollu.titles.council')}}</label>
              <multiselect
                v-model="patientDetailsToEdit.municipio"
                :options="dcounties"
                label="name"
                track-by="municipioId"
                :placeholder="$t('dashboard_new_evollu.titles.council')"
                @input="onChangeDCounty"
              />
            </div>
          </div>

          <div class="row" v-if="patientDetailsToEdit.country && patientDetailsToEdit.country !== 'Portugal'">
            <div class="col-12">
              <va-input
                v-model="patientDetailsToEdit.county"
                :label="$t('dashboard_new_evollu.titles.county')"
                :placeholder="$t('dashboard_new_evollu.titles.county')"
              />
            </div>
          </div>

        </form>
    </va-modal>
  </section>
</template>
<script>
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/StandardErrors.js'
import { EMAIL_REGEX } from '@/utils/regex'
import { mapState } from "vuex";
import { VueTelInput } from 'vue-tel-input'
import moment from "moment";
import {OneCareService} from "@/api/OneCareService";
import LoadingAnimation from "@/components/common/LoadingAnimation.vue";

export default {
  components: {
    LoadingAnimation,
    Multiselect,
    VueTelInput
  },
  props: {
    patientDetailsToEdit: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isLoading: true,
      performingRequest: false,
      showModal: false,
      userForm: {
        errors: new Errors(),
      },
      invalidMobilePhone: false,
      titles: [
        "Sr",
        "Sra",
        "Dr",
        "Dra",
        "Mr",
        "Mrs",
        "Ms"
      ],
      countries: [
        "Portugal",
        "Espanha",
        "Reino Unido",
        "Bélgica",
        "Grécia"
      ],
      districts: [],
      dcounties: [],
      districtSelected: null,
    }
  },
  created: async function () {
    this.isLoading = true
    await OneCareService.getDistricts()
      .then(response => {
        this.districts = response.map((district) => ({
          municipioId: null,
          name: null,
          districtID: district.idDistrito,
          districtName: district.nomeDistrito,
          zipCodes: null
        }))
          .sort((v1, v2) => { return v1.districtName.normalize("NFD") < v2.districtName.normalize("NFD") ? -1 : 1 })
      })
    this.districtSelected = this.patientDetailsToEdit.municipio
    if (this.districtSelected.districtID) {
      await this.updateDCounties(this.districtSelected.districtID)
    }
    this.isLoading = false
  },
  mounted: function () {
    this.showModal = true
  },
  methods: {
    telephoneValidation: function (data) {
      this.invalidMobilePhone = !data.isValid
      if (data.isValid) {
        //this.patientDetailsToEdit.mobile = data.number.significant
        this.patientDetailsToEdit.fullMobileNumber = data.number.international
        this.patientDetailsToEdit.mobile = data.number.significant
        this.patientDetailsToEdit.countryCallingCode = data.number.international.split(' ')[0]
      }
    },
    submitForm: async function () {
      this.performingRequest = true

      await OneCareService.postPatient(this.patientDetailsToEdit)
        .then(() => {
          this.showToast(this.$t('dashboard_new_evollu.messages.successful_edit'), this.$store.state.successToastOptions)
          this.onCancel()
        })
        .catch(errors => {
          console.error(errors)
          this.showToast(this.$t('dashboard_new_evollu.messages.something_went_wrong_edit'), this.$store.state.errorToastOptions)
          this.performingRequest = false
        })
    },
    onCancel: function () {
      this.showModal = false
      setTimeout(() => {
        this.$emit('cancel')
      }, 500)
    },
    updateDCounties: async function (districtId) {
      let district = await OneCareService.getCounties(districtId)
      this.dcounties = district.municipios.sort((v1, v2) => { return v1.name.normalize("NFD") < v2.name.normalize("NFD") ? -1 : 1 })
    },
    onChangeDistrict: async function (district) {
      this.isLoading = true
      await this.updateDCounties(district.districtID)
      this.patientDetailsToEdit.municipio = this.dcounties[0]
      this.districtSelected = this.patientDetailsToEdit.municipio
      this.isLoading = false
    },
    onChangeDCounty: function (dcounty) {
      this.patientDetailsToEdit.municipio = dcounty
      this.districtSelected = this.patientDetailsToEdit.municipio
    },
  },
  computed: {
    moment() {
      return moment
    },
    ...mapState({
      'countriestel': (state) => state.countries
    }),
    invalidEmail: function () {
      if (this.patientDetailsToEdit.email) {
        if(!this.patientDetailsToEdit.email.match(EMAIL_REGEX)) {
          //this.formErrors.record({ 'person.phone': ['The person.phone field is invalid.'] }, errorsPrefix)
          this.userForm.errors.record({ 'email': ['Email must be a valid email.'] }, 'people.errors.')
        } else {
          this.userForm.errors.clear('email')
        }
        return !this.patientDetailsToEdit.email.match(EMAIL_REGEX)
      }
      return false
    },
    anyErrors: function () {
      return this.invalidEmail || this.invalidMobilePhone || this.userForm.errors.length > 0
    },
    defaultCountrySelection: function () {
      return this.$store.state.countries.default
    },
    //parsedZipCode: this.patientDetailsToEdit !== null ? this.patientDetailsToEdit.zipPrefix + "-" + this.patientDetailsToEdit.zipSufix : "",
  },
}
</script>
